import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { Bug, Lightbulb, Mail, Send } from 'lucide-react'
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { COLLECTIONS, type ReportType } from '@shared/model'
import {
  Button,
  Card,
  EmptyState,
  Field,
  SearchField,
  SectionTitle,
  SegmentedControl,
  Textarea,
} from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { useToast } from '@/components/Toast'
import {
  HELP_LEVEL_INTROS,
  HELP_LEVEL_LABELS,
  HELP_TOPICS,
  topicEnabled,
  levelsForRole,
  searchHelp,
  type HelpLevel,
} from '@/lib/help'
import { useFeatures } from '@/lib/features'
import { useContact } from '@/hooks/useContact'

/**
 * Hjälp för alla nivåer.
 *
 * Sidan visar bara de nivåer rollen faktiskt kan behöva. En köpare som möts
 * av tolv frågor om backup och rollbyten läser ingen av dem – och slutar leta
 * efter den fråga hen faktiskt hade.
 *
 * Köpar- och säljarhjälpen visas för alla, eftersom vem som helst kan bli
 * förälder med fyra häften att sälja utan att någon roll ändras.
 */
export function HelpPage() {
  const { role } = useAuth()
  const { hash } = useLocation()
  const { features } = useFeatures()
  const contact = useContact()

  const levels = useMemo(() => levelsForRole(role), [role])
  const [level, setLevel] = useState<HelpLevel>(levels[0])
  const [search, setSearch] = useState('')

  /*
   * Djuplänkar. /hjalp#stamma-av-swish hoppar till rätt fråga och fäller ut
   * den, så att en knapp någon annanstans i appen kan peka hit i stället för
   * att upprepa förklaringen på plats.
   */
  useEffect(() => {
    const id = hash.replace('#', '')
    if (!id) return

    const topic = HELP_TOPICS.find((item) => item.id === id)
    if (!topic) return

    setLevel(topic.level)
    setSearch('')

    // Efter att nivån bytts måste elementet hinna renderas.
    const timer = window.setTimeout(() => {
      const element = document.getElementById(id)
      if (!(element instanceof HTMLDetailsElement)) return
      element.open = true
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 60)
    return () => window.clearTimeout(timer)
  }, [hash])

  const searching = search.trim().length > 0

  const visible = useMemo(() => {
    // Sökningen går över alla nivåer man har tillgång till. Den som letar vet
    // sällan vilken nivå svaret råkar ligga på.
    // Avsnitt om avstängda funktioner göms även i sökningen. Hjälp om något
    // som inte finns skickar folk att leta efter en knapp som aldrig kommer.
    const pool = HELP_TOPICS.filter(
      (topic) => levels.includes(topic.level) && topicEnabled(topic, features),
    )
    return searching ? searchHelp(pool, search) : pool.filter((topic) => topic.level === level)
  }, [levels, level, search, searching, features])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-extrabold">Hjälp</h1>
        <p className="text-sm text-muted">Hur allt fungerar, och en väg in till oss.</p>
      </div>

      <SearchField
        value={search}
        onChange={setSearch}
        placeholder="Sök i hjälpen"
        label="Sök i hjälpen"
      />

      {!searching && levels.length > 1 && (
        <SegmentedControl<HelpLevel>
          label="Välj nivå"
          value={level}
          onChange={setLevel}
          options={levels.map((item) => ({ value: item, label: HELP_LEVEL_LABELS[item] }))}
        />
      )}

      {!searching && <p className="text-sm text-muted">{HELP_LEVEL_INTROS[level]}</p>}

      {searching && (
        <p className="text-sm text-muted">
          {visible.length === 0
            ? 'Inga träffar.'
            : `${visible.length} träff${visible.length === 1 ? '' : 'ar'} i hela hjälpen.`}
        </p>
      )}

      {visible.length === 0 ? (
        <EmptyState
          title="Vi hittar inget svar på det"
          description="Prova ett annat ord, eller skriv till oss längre ned på sidan så hjälper vi dig."
        />
      ) : (
        <section className="space-y-2.5">
          {visible.map((topic) => (
            <details
              key={topic.id}
              id={topic.id}
              className="group scroll-mt-24 rounded-card border border-border bg-surface px-4 py-3 shadow-card"
            >
              <summary className="flex min-h-11 cursor-pointer list-none items-center font-display font-extrabold text-ink marker:hidden">
                <span className="flex items-center justify-between gap-3">
                  <span>
                    {topic.question}
                    {searching && (
                      <span className="ml-2 text-xs font-semibold text-muted">
                        {HELP_LEVEL_LABELS[topic.level]}
                      </span>
                    )}
                  </span>
                  <span
                    aria-hidden
                    className="shrink-0 text-brand-ink transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </span>
              </summary>

              <p className="mt-2 text-sm text-muted">{topic.answer}</p>

              {topic.steps && (
                <ol className="mt-3 space-y-1.5">
                  {topic.steps.map((step, index) => (
                    <li key={step} className="flex gap-2.5 text-sm">
                      <span className="flex size-5 shrink-0 items-center justify-center rounded-pill bg-brand-soft text-xs font-bold text-brand-ink">
                        {index + 1}
                      </span>
                      <span className="text-muted">{step}</span>
                    </li>
                  ))}
                </ol>
              )}
            </details>
          ))}
        </section>
      )}

      <ReportForm />

      <Card className="p-5">
        <SectionTitle>Kontakt</SectionTitle>
        <p className="text-sm text-muted">
          Hittar du inte svaret här? Mejla{' '}
          <a
            href={`mailto:${contact.officeEmail}`}
            className="font-semibold text-brand-ink underline"
          >
            {contact.officeEmail}
          </a>{' '}
          så hjälper vi dig.
        </p>
      </Card>
    </div>
  )
}

/** Buggar och förslag, som hamnar under Rapporter i adminytan. */
function ReportForm() {
  const { user } = useAuth()
  const toast = useToast()

  const [type, setType] = useState<ReportType>('bug')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!message.trim()) return

    setBusy(true)
    try {
      await addDoc(collection(db, COLLECTIONS.reports), {
        type,
        message: message.trim(),
        status: 'new',
        userUid: user?.uid ?? null,
        userEmail: user?.email ?? null,
        page: window.location.pathname,
        userAgent: navigator.userAgent,
        createdAt: serverTimestamp(),
      })
      toast.success('Tack! Vi har fått din rapport.')
      setMessage('')
    } catch {
      toast.error('Kunde inte skicka just nu. Försök igen om en stund.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="p-5">
      <SectionTitle>Hittat en bugg eller har en idé?</SectionTitle>
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
        <SegmentedControl<ReportType>
          label="Vad gäller det?"
          value={type}
          onChange={setType}
          options={[
            {
              value: 'bug',
              label: (
                <>
                  <Bug size={14} aria-hidden />
                  Något är fel
                </>
              ),
            },
            {
              value: 'idea',
              label: (
                <>
                  <Lightbulb size={14} aria-hidden />
                  Ett förslag
                </>
              ),
            },
          ]}
        />

        <Field
          label="Berätta"
          hint="Skriv gärna vad du gjorde och vad du förväntade dig i stället."
        >
          {({ id }) => (
            <Textarea
              id={id}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Jag försökte lösa in en rabatt men…"
            />
          )}
        </Field>

        <Button
          type="submit"
          loading={busy}
          disabled={!message.trim()}
          icon={<Send size={17} />}
        >
          Skicka
        </Button>

        <p className="flex items-start gap-1.5 text-xs text-muted">
          <Mail size={13} className="mt-0.5 shrink-0" aria-hidden />
          Vi ser din e-postadress och vilken sida du var på, så att vi kan svara och förstå vad
          som hänt.
        </p>
      </form>
    </Card>
  )
}
